import { create } from 'zustand';

interface WebSocketStore {
  // Tradovate WebSocket timestamp
  lastMessageTime: number | null;
  setLastMessageTime: (time: number) => void;

  // Market data WebSocket timestamp
  lastMarketDataTime: number | null;
  setLastMarketDataTime: (time: number) => void;

  // WebSocket Status
  websocketStatus: 'connecting' | 'connected' | 'disconnected' | 'working' | 'connected-partial' | 'working-partial';
  setWebsocketStatus: (status: 'connecting' | 'connected' | 'disconnected' | 'working' | 'connected-partial' | 'working-partial') => void;

  // Reconnection state with exponential backoff
  reconnectDelay: number;
  reconnectAttempts: number;
  maxReconnectDelay: number;
  setReconnectDelay: (delay: number) => void;
  incrementReconnectDelay: () => void;
  resetReconnectDelay: () => void;
}

// Constants for exponential backoff
const INITIAL_RECONNECT_DELAY = 1000; // 1 second
const MAX_RECONNECT_DELAY = 30000; // 30 seconds

export const useWebSocketStore = create<WebSocketStore>((set, get) => ({
  // Tradovate WebSocket timestamp.
  // Second-granularity: these are liveness timestamps written from the frame
  // path (market_data arrives 500+/min) — an unthrottled set() re-renders
  // every whole-store subscriber per frame for a value no UI reads faster
  // than once a second.
  lastMessageTime: null,
  setLastMessageTime: (time) => {
    if (time - (get().lastMessageTime ?? 0) < 1000) return;
    set({ lastMessageTime: time });
  },

  // Market data WebSocket timestamp (same second-granularity contract)
  lastMarketDataTime: null,
  setLastMarketDataTime: (time) => {
    if (time - (get().lastMarketDataTime ?? 0) < 1000) return;
    set({ lastMarketDataTime: time });
  },

  // WebSocket Status
  websocketStatus: 'connecting',
  setWebsocketStatus: (status) => set({ websocketStatus: status }),

  // Reconnection state with exponential backoff
  reconnectDelay: INITIAL_RECONNECT_DELAY,
  reconnectAttempts: 0,
  maxReconnectDelay: MAX_RECONNECT_DELAY,

  setReconnectDelay: (delay) => set({ reconnectDelay: delay }),

  // Increment delay with exponential backoff (double each time, cap at max)
  incrementReconnectDelay: () => set((state) => ({
    reconnectDelay: Math.min(state.reconnectDelay * 2, MAX_RECONNECT_DELAY),
    reconnectAttempts: state.reconnectAttempts + 1,
  })),

  // Reset delay on successful connection
  resetReconnectDelay: () => set({
    reconnectDelay: INITIAL_RECONNECT_DELAY,
    reconnectAttempts: 0,
  }),
})); 